import { useField } from 'formik';
import Fuse from 'fuse.js';
import { ComponentProps, FC, ReactNode, useMemo, useState } from 'react';

import { Autocomplete, AutocompleteItem } from 'src/general/components/Autocomplete/Autocomplete';
import { FormikOption } from 'src/general/components/forms/FormikInput/FormikInput.types';
import { InputCheckboxFormik } from 'src/general/components/forms/Input/InputCheckboxFormik';
import { boldMatchCharacters } from 'src/general/helpers/fuzzySearchHelper';

import { FilterOptionHierarchy } from './PlpFilters.type';
import { PlpFiltersHierarchy, toggleFilterHierarchyValue } from './PlpFiltersHierarchy';
import styles from './PlpFiltersMake.module.scss';

type DisplayMode = 'popular' | 'all';

type SearchItem = AutocompleteItem<string> & { keyword: string; type: 'Make' | 'Model' };

const flattenOptions = (options: FilterOptionHierarchy[], fieldValue: string[] = []) => {
  const flattenedOptions: FormikOption[] = [];

  if (fieldValue) {
    options.forEach((make) => {
      if (fieldValue.findIndex((singleValue) => singleValue === make.value) >= 0) {
        flattenedOptions.push({ ...make, label: <SelectedOptionLabel label={make.label} type="Make" /> });
      }

      flattenChildOptions(make, flattenedOptions, fieldValue);
    });
  }
  return flattenedOptions.sort((a, b) => (a.value < b.value ? -1 : 1));
};

const flattenChildOptions = (make: FilterOptionHierarchy, flattenedOptions: FormikOption[], fieldValue: string[]) => {
  if (make.children.length) {
    make.children.forEach((model) => {
      if (fieldValue.findIndex((singleValue) => singleValue === model.value && model.value !== make.value) >= 0) {
        flattenedOptions.push({
          ...model,
          label: <SelectedOptionLabel label={`${make.labelNoCount} ${model.label}`} type="Model" />,
        });
      }
    });
  }
};

export const PlpFiltersMake: FC<ComponentProps<typeof PlpFiltersHierarchy>> = ({ options, ...props }) => {
  const [field] = useField<string[]>(props.name);
  const [displayMode, setDisplayMode] = useState<DisplayMode>('popular');
  const fieldValue = field.value ?? [];
  const flattenedOptions: FormikOption[] = useMemo(() => flattenOptions(options, field.value), [options, field.value]);

  const filteredOptions = useMemo(
    () =>
      displayMode === 'popular'
        ? options.slice(0, 8)
        : options
            .filter(
              (option) =>
                option.children.length > 1 && flattenedOptions.findIndex((i) => i.value === option.value) === -1,
            ) //Filter makes with quantity = 0 and not selected. Makes with 0 quantity will still show selected and not in all makes A-Z if it exists in url
            .sort((a, b) => (a.value > b.value ? 1 : -1)),
    [displayMode, options, flattenedOptions],
  );

  const fuseIndex = useMemo(() => generateFuseIndex(options), ['options']);

  const getItems = async (value: string): Promise<AutocompleteItem<string>[]> => {
    const results = fuseIndex.search(value);

    const items: AutocompleteItem<string>[] = results
      .map(({ item, matches }) => ({
        ...item,
        label: <SelectedOptionLabel label={boldMatchCharacters(`${item.label}`, matches)} type={item.type} />,
      }))
      .filter((item) => fieldValue.indexOf(item.value) < 0)
      // Limit the results to 5
      .splice(0, 5);

    return items;
  };

  return (
    <div className={styles.root}>
      <MakeModelAutocomplete {...props} options={options} getItems={getItems} disablePortal={props.type === 'modal'} />
      {/* Selected options */}
      {flattenedOptions.length > 0 && (
        <div className={styles.selectedOptions}>
          <InputCheckboxFormik id={`${props.id}-flattened`} name={props.name} options={flattenedOptions} />
        </div>
      )}
      {/* Options list */}
      <p className={styles.listTitle}>{displayMode === 'popular' ? 'Popular makes' : 'All makes A-Z'}</p>
      <PlpFiltersHierarchy {...props} options={filteredOptions} />
      {/* Option display toggle */}
      <button
        className={styles.listSwitch}
        type="button"
        data-testid="PlpFiltersMake-displaySwitch"
        onClick={() => {
          setDisplayMode(displayMode === 'popular' ? 'all' : 'popular');
        }}
      >
        {displayMode === 'popular' ? 'See all makes' : 'See popular makes'}
      </button>
    </div>
  );
};

type MakeModelAutocompleteProps = ComponentProps<typeof PlpFiltersHierarchy> & {
  getItems: (value: string) => Promise<AutocompleteItem<string>[]>;
  disablePortal?: boolean;
};

const MakeModelAutocomplete: FC<MakeModelAutocompleteProps> = ({ id, name, getItems, disablePortal }) => {
  const [field, _meta, helpers] = useField<string[]>(name);
  const fieldValue = field.value ?? [];

  const onSelectItem = (value: string) => {
    const newFieldValue = toggleFilterHierarchyValue(value, [...fieldValue]);
    helpers.setValue(newFieldValue);
  };

  return (
    <Autocomplete<string>
      id={`${id}-autocomplete`}
      name={name}
      placeholder="Search makes, models"
      getItems={getItems}
      onSelectItem={onSelectItem}
      disablePortal={disablePortal}
    />
  );
};

const generateFuseIndex = (options: FilterOptionHierarchy[]) => {
  const collection: SearchItem[] = [];

  options.forEach((make) => {
    collection.push({
      key: make.value,
      label: `${make.label}`,
      value: make.value,
      keyword: `${make.labelNoCount}`,
      type: 'Make',
    });

    make.children.forEach((model) => {
      // Skip the "All option"
      if (make.value !== model.value) {
        collection.push({
          key: model.value,
          label: `${make.labelNoCount} ${model.label}`,
          value: model.value,
          keyword: `${make.labelNoCount} ${model.labelNoCount}`,
          type: 'Model',
        });
      }
    });
  });

  const fuseOptions: Fuse.IFuseOptions<SearchItem> = {
    includeMatches: true,
    includeScore: true,
    threshold: 0.45,
    ignoreLocation: true,
    keys: ['keyword'],
  };
  const fuse = new Fuse<SearchItem>(collection, fuseOptions);
  return fuse;
};

const SelectedOptionLabel: FC<{ label: ReactNode; type: string }> = ({ label, type }) => (
  <div className={styles.selectedOption}>
    <div className={styles.selectedOptionLabel}>{label}</div>
    <div className={styles.selectedOptionType}>{type}</div>
  </div>
);
